import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Shield, TrendingUp, GraduationCap, CheckCircle, Truck, FileCheck, DollarSign, BarChart3, BookOpen, Video, Award, Users, Clock, Calendar, Wrench, Calculator, FileText, Download, HelpCircle } from 'lucide-react';
import SEO from '../components/shared/SEO';

const InsideTheHivePage = () => {
  return (
    <div data-testid="inside-the-hive-page" className="bg-[#0A0A0A]">
      <SEO 
        title="Inside The Hive | Queen B Logistics"
        description="Explore The Hive platform - Build your authority, Protect your compliance, Grow your business, master the industry through Academy, and access essential Resources. Your complete trucking operations command center."
        canonical="https://queenblogistics.com/inside-the-hive"
        keywords="carrier dashboard, dispatch management, trucking compliance, freight operations platform, owner operator tools, trucking academy, trucking resources"
      />
      
      {/* HERO SECTION */}
      <section className="relative py-20 md:py-32" data-testid="hive-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-8">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
              <span className="text-[#D4AF37] text-sm font-medium tracking-wider uppercase">
                The Hive Platform
              </span>
            </div>
            
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase leading-none mb-4">
              Inside The <span className="text-[#D4AF37]">Hive</span>
            </h1>
            
            <p className="text-white/70 text-xl md:text-2xl mb-8">
              A Command Center Built for Serious Carriers
            </p>
            
            <p className="text-white/50 text-lg max-w-2xl leading-relaxed mb-8">
              The Hive is your complete operations hub — from launching your authority with <span className="text-[#CD7F32]">Build</span>, 
              staying compliant with <span className="text-[#C0C0C0]">Protect</span>, scaling your dispatch with <span className="text-[#D4AF37]">Grow</span>, 
              mastering the industry through <span className="text-purple-400">Academy</span>, to accessing essential tools in <span className="text-pink-400">Resources</span>. Everything you need, organized in one place.
            </p>

            {/* Quick Nav Pills */}
            <div className="flex flex-wrap gap-3">
              <a href="#build-section" className="px-4 py-2 bg-[#CD7F32]/20 border border-[#CD7F32]/40 text-[#CD7F32] text-sm font-medium hover:bg-[#CD7F32]/30 transition-colors">
                Build
              </a>
              <a href="#protect-section" className="px-4 py-2 bg-[#C0C0C0]/20 border border-[#C0C0C0]/40 text-[#C0C0C0] text-sm font-medium hover:bg-[#C0C0C0]/30 transition-colors">
                Protect
              </a>
              <a href="#grow-section" className="px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-sm font-medium hover:bg-[#D4AF37]/30 transition-colors">
                Grow
              </a>
              <a href="#academy-section" className="px-4 py-2 bg-purple-500/20 border border-purple-500/40 text-purple-400 text-sm font-medium hover:bg-purple-500/30 transition-colors">
                Academy
              </a>
              <a href="#resources-section" className="px-4 py-2 bg-pink-500/20 border border-pink-500/40 text-pink-400 text-sm font-medium hover:bg-pink-500/30 transition-colors">
                Resources
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== BUILD SECTION ==================== */}
      <section id="build-section" className="py-16 md:py-24 bg-[#050505]" data-testid="hive-build-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#CD7F32]/10 border border-[#CD7F32]/30 mb-6">
                <Building2 className="w-4 h-4 text-[#CD7F32]" />
                <span className="text-[#CD7F32] text-sm font-medium tracking-wider uppercase">
                  Build Module
                </span>
              </div>
              
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
                Launch Your <span className="text-[#CD7F32]">Authority</span>
              </h2>
              
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                The Build module is your guided path to getting your MC authority and becoming a legally operating carrier. 
                Our phase-tracked system walks you through every step — from business formation to your first load.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Phase-by-phase authority launch tracking',
                  'Document checklist with upload tracking',
                  'Business formation guidance',
                  'Insurance & compliance setup',
                  '30/60/90-day launch support based on tier'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#CD7F32] flex-shrink-0" />
                    <span className="text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/build"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#CD7F32] hover:bg-[#CD7F32]/80 text-black font-bold uppercase tracking-wider transition-colors"
              >
                Explore Build
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Right - Build Progress Preview */}
            <div className="bg-[#121212] border border-[#CD7F32]/20 p-6">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-[#1A1A1A] border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
                  <div className="flex-1 ml-4">
                    <div className="bg-white/10 rounded px-3 py-1 text-white/30 text-xs max-w-[200px]">
                      hive.queenblogistics.com/build
                    </div>
                  </div>
                </div>
                
                {/* Build Dashboard Content */}
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-[#CD7F32]" />
                      <span className="text-white font-bold">Build Progress</span>
                    </div>
                    <span className="text-[#CD7F32] text-xs font-bold px-2 py-1 bg-[#CD7F32]/10 rounded">ELITE PACKAGE</span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/60 text-xs">Phase 3 of 5</span>
                      <span className="text-[#CD7F32] text-sm font-bold">68%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full w-[68%] bg-gradient-to-r from-[#CD7F32] to-[#D4AF37] rounded-full" />
                    </div>
                  </div>
                  
                  {/* Phase List */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-2 bg-green-500/10 rounded">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white/70 text-sm">Phase 1: Intake</span>
                      <span className="ml-auto text-green-400 text-xs">Complete</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-green-500/10 rounded">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white/70 text-sm">Phase 2: Formation</span>
                      <span className="ml-auto text-green-400 text-xs">Complete</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-[#CD7F32]/20 rounded border border-[#CD7F32]/30">
                      <div className="w-6 h-6 rounded-full bg-[#CD7F32] flex items-center justify-center">
                        <span className="text-black text-xs font-bold">3</span>
                      </div>
                      <span className="text-white text-sm font-medium">Phase 3: Authority</span>
                      <span className="ml-auto text-[#CD7F32] text-xs">In Progress</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 rounded opacity-50">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-white/60 text-xs">4</span>
                      </div>
                      <span className="text-white/40 text-sm">Phase 4: Compliance Setup</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 rounded opacity-50">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-white/60 text-xs">5</span>
                      </div>
                      <span className="text-white/40 text-sm">Phase 5: Launch Activation</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-sm text-center mt-4">
                Phase-tracked authority launch system
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROTECT SECTION ==================== */}
      <section id="protect-section" className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="hive-protect-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Compliance Preview */}
            <div className="order-2 lg:order-1 bg-[#121212] border border-[#C0C0C0]/20 p-6">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-[#1A1A1A] border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
                  <div className="flex-1 ml-4">
                    <div className="bg-white/10 rounded px-3 py-1 text-white/30 text-xs max-w-[200px]">
                      hive.queenblogistics.com/protect
                    </div>
                  </div>
                </div>
                
                {/* Compliance Dashboard Content */}
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#C0C0C0]" />
                      <span className="text-white font-bold">Compliance Monitor</span>
                    </div>
                    <span className="text-green-400 text-xs font-bold px-2 py-1 bg-green-400/10 rounded">ALL CURRENT</span>
                  </div>
                  
                  {/* Health Score */}
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                    <div className="relative w-16 h-16">
                      <svg className="w-16 h-16 transform -rotate-90">
                        <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" className="text-white/10" />
                        <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" className="text-green-500" strokeDasharray="176" strokeDashoffset="3.5" strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-green-400 font-bold text-lg">98%</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-white font-medium">Health Score</div>
                      <div className="text-white/40 text-sm">Excellent standing</div>
                    </div>
                  </div>
                  
                  {/* Compliance Items */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-[#C0C0C0]" />
                        <span className="text-white/70 text-sm">Auto Insurance</span>
                      </div>
                      <span className="text-green-400 text-xs px-2 py-0.5 bg-green-400/10 rounded">Current</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-[#C0C0C0]" />
                        <span className="text-white/70 text-sm">IFTA Registration</span>
                      </div>
                      <span className="text-green-400 text-xs px-2 py-0.5 bg-green-400/10 rounded">Current</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-[#C0C0C0]" />
                        <span className="text-white/70 text-sm">UCR Filing</span>
                      </div>
                      <span className="text-yellow-400 text-xs px-2 py-0.5 bg-yellow-400/10 rounded">Due in 30 days</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-[#C0C0C0]" />
                        <span className="text-white/70 text-sm">Drug & Alcohol Program</span>
                      </div>
                      <span className="text-green-400 text-xs px-2 py-0.5 bg-green-400/10 rounded">Current</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-sm text-center mt-4">
                Automated compliance tracking & alerts
              </p>
            </div>
            
            {/* Right Content */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 mb-6">
                <Shield className="w-4 h-4 text-[#C0C0C0]" />
                <span className="text-[#C0C0C0] text-sm font-medium tracking-wider uppercase">
                  Protect Module
                </span>
              </div>
              
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
                Stay <span className="text-[#C0C0C0]">Compliant</span>
              </h2>
              
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                The Protect module keeps your authority safe by tracking every compliance deadline and document expiration. 
                Never miss a filing date or let your insurance lapse — our system alerts you before problems happen.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Real-time compliance health score',
                  'Automatic expiration date tracking',
                  'Proactive deadline alerts (30/14/7 days)',
                  'Secure document vault storage',
                  'IFTA, UCR, IRP, BOC-3 monitoring'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C0C0C0] flex-shrink-0" />
                    <span className="text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/protect"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C0C0C0] hover:bg-[#C0C0C0]/80 text-black font-bold uppercase tracking-wider transition-colors"
              >
                Explore Protect
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== GROW SECTION ==================== */}
      <section id="grow-section" className="py-16 md:py-24 bg-[#050505]" data-testid="hive-grow-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
                <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-medium tracking-wider uppercase">
                  Grow Module
                </span>
              </div>
              
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
                Scale Your <span className="text-[#D4AF37]">Business</span>
              </h2>
              
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                The Grow module is your dispatch command center. Track loads, analyze rates, monitor revenue, 
                and get the insights you need to maximize profitability. From Bronze to Gold tier, 
                we scale with your operation.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Real-time load tracking & status',
                  'Revenue & rate per mile analytics',
                  'Broker relationship management',
                  'Weekly/monthly performance reports',
                  'Dedicated dispatch support (Silver/Gold)'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/grow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] hover:bg-[#D4AF37]/80 text-black font-bold uppercase tracking-wider transition-colors"
              >
                Explore Grow
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Right - Dispatch Dashboard Preview */}
            <div className="bg-[#121212] border border-[#D4AF37]/20 p-6">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-[#1A1A1A] border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
                  <div className="flex-1 ml-4">
                    <div className="bg-white/10 rounded px-3 py-1 text-white/30 text-xs max-w-[200px]">
                      hive.queenblogistics.com/grow
                    </div>
                  </div>
                </div>
                
                {/* Dispatch Dashboard Content */}
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Truck className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white font-bold">Dispatch Dashboard</span>
                    </div>
                    <span className="text-[#D4AF37] text-xs font-bold px-2 py-1 bg-[#D4AF37]/10 rounded">GOLD TIER</span>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#D4AF37]/10 p-3 rounded-lg border border-[#D4AF37]/20">
                      <div className="text-white/40 text-[10px] uppercase tracking-wide">Weekly Revenue</div>
                      <div className="text-[#D4AF37] font-bold text-2xl mt-1">$8,420</div>
                      <div className="flex items-center gap-1 mt-1">
                        <TrendingUp className="w-3 h-3 text-green-400" />
                        <span className="text-green-400 text-xs">+18%</span>
                      </div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg">
                      <div className="text-white/40 text-[10px] uppercase tracking-wide">Avg RPM</div>
                      <div className="text-green-400 font-bold text-2xl mt-1">$3.24</div>
                      <div className="text-white/40 text-xs mt-1">Above market</div>
                    </div>
                  </div>
                  
                  {/* Revenue Chart */}
                  <div className="bg-white/5 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white/60 text-xs">Weekly Performance</span>
                      <span className="text-white/40 text-xs">Last 7 days</span>
                    </div>
                    <div className="flex items-end gap-1 h-16">
                      {[40, 60, 75, 55, 90, 100, 85].map((h, i) => (
                        <div key={i} className="flex-1 bg-[#D4AF37] rounded-t transition-all hover:bg-[#F5D47B]" style={{height: `${h}%`, opacity: 0.3 + (i * 0.1)}} />
                      ))}
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-white/30 text-[10px]">Mon</span>
                      <span className="text-white/30 text-[10px]">Sun</span>
                    </div>
                  </div>
                  
                  {/* Active Loads */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#D4AF37]/20 rounded flex items-center justify-center">
                        <Truck className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div>
                        <div className="text-white text-sm font-medium">4 Active Loads</div>
                        <div className="text-white/40 text-xs">2 in transit, 2 pending</div>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/40" />
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-sm text-center mt-4">
                Real-time dispatch & revenue tracking
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ACADEMY SECTION ==================== */}
      <section id="academy-section" className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="hive-academy-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Academy Preview */}
            <div className="order-2 lg:order-1 bg-[#121212] border border-purple-500/20 p-6">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-[#1A1A1A] border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
                  <div className="flex-1 ml-4">
                    <div className="bg-white/10 rounded px-3 py-1 text-white/30 text-xs max-w-[200px]">
                      hive.queenblogistics.com/academy
                    </div>
                  </div>
                </div>
                
                {/* Academy Dashboard Content */}
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-purple-400" />
                      <span className="text-white font-bold">Hive Academy</span>
                    </div>
                    <span className="text-purple-400 text-xs font-bold px-2 py-1 bg-purple-400/10 rounded">PRO ACCESS</span>
                  </div>
                  
                  {/* Featured Course */}
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-purple-400 text-xs font-bold uppercase">Featured Course</span>
                      <span className="text-yellow-400 text-[10px] px-1.5 py-0.5 bg-yellow-400/10 rounded">New</span>
                    </div>
                    <h4 className="text-white font-bold mb-1">Authority Launch Masterclass</h4>
                    <p className="text-white/50 text-xs mb-2">Complete guide to getting your MC authority and running legally.</p>
                    <div className="flex items-center gap-4 text-white/40 text-xs">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 4.5 hours</span>
                      <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> 12 lessons</span>
                    </div>
                  </div>
                  
                  {/* Course Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-[#D4AF37]/20 rounded mb-2 flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div className="text-white text-xs font-medium">Rate Negotiation</div>
                      <div className="text-white/40 text-[10px] mt-1">8 lessons</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-[#C0C0C0]/20 rounded mb-2 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-[#C0C0C0]" />
                      </div>
                      <div className="text-white text-xs font-medium">Compliance 101</div>
                      <div className="text-white/40 text-[10px] mt-1">10 lessons</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-blue-500/20 rounded mb-2 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="text-white text-xs font-medium">Load Boards</div>
                      <div className="text-white/40 text-[10px] mt-1">6 lessons</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-green-500/20 rounded mb-2 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-green-400" />
                      </div>
                      <div className="text-white text-xs font-medium">Tax Strategies</div>
                      <div className="text-white/40 text-[10px] mt-1">9 lessons</div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
                    <div className="text-center">
                      <div className="text-purple-400 font-bold text-lg">50+</div>
                      <div className="text-white/40 text-[10px] uppercase">Courses</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple-400 font-bold text-lg">200+</div>
                      <div className="text-white/40 text-[10px] uppercase">Lessons</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple-400 font-bold text-lg">24/7</div>
                      <div className="text-white/40 text-[10px] uppercase">Access</div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-sm text-center mt-4">
                On-demand training & industry courses
              </p>
            </div>
            
            {/* Right Content */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 mb-6">
                <GraduationCap className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium tracking-wider uppercase">
                  Academy Module
                </span>
              </div>
              
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
                Master The <span className="text-purple-400">Industry</span>
              </h2>
              
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                The Academy is your on-demand learning center for everything trucking. From getting started 
                to advanced strategies, our video courses and guides help you build the knowledge to succeed. 
                Learn at your own pace, 24/7.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'On-demand video courses & tutorials',
                  'Step-by-step guides & checklists',
                  'Industry best practices & strategies',
                  'Rate negotiation & broker tactics',
                  'Tax planning & business growth'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <span className="text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/academy"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-bold uppercase tracking-wider transition-colors"
              >
                Explore Academy
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== RESOURCES SECTION ==================== */}
      <section id="resources-section" className="py-16 md:py-24 bg-[#050505]" data-testid="hive-resources-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 border border-pink-500/30 mb-6">
                <Wrench className="w-4 h-4 text-pink-400" />
                <span className="text-pink-400 text-sm font-medium tracking-wider uppercase">
                  Resources Module
                </span>
              </div>
              
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
                Essential <span className="text-pink-400">Tools</span>
              </h2>
              
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                The Resources module gives you instant access to essential trucking tools, calculators, 
                templates, and documents. From trip cost calculators to rate sheets and compliance templates — 
                everything you need at your fingertips.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Trip cost & fuel calculators',
                  'Rate per mile analysis tools',
                  'Downloadable templates & forms',
                  'Compliance document library',
                  'Industry contacts & directories'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0" />
                    <span className="text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/resources"
                className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white font-bold uppercase tracking-wider transition-colors"
              >
                Explore Resources
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Right - Resources Preview */}
            <div className="bg-[#121212] border border-pink-500/20 p-6">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-[#1A1A1A] border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
                  <div className="flex-1 ml-4">
                    <div className="bg-white/10 rounded px-3 py-1 text-white/30 text-xs max-w-[200px]">
                      hive.queenblogistics.com/resources
                    </div>
                  </div>
                </div>
                
                {/* Resources Dashboard Content */}
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-pink-400" />
                      <span className="text-white font-bold">Resource Center</span>
                    </div>
                    <span className="text-pink-400 text-xs font-bold px-2 py-1 bg-pink-400/10 rounded">FULL ACCESS</span>
                  </div>
                  
                  {/* Calculator Preview */}
                  <div className="bg-pink-500/10 border border-pink-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="w-4 h-4 text-pink-400" />
                      <span className="text-white font-medium text-sm">Trip Cost Calculator</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/5 p-2 rounded">
                        <div className="text-white/40 text-[10px] uppercase">Distance</div>
                        <div className="text-white font-bold">847 mi</div>
                      </div>
                      <div className="bg-white/5 p-2 rounded">
                        <div className="text-white/40 text-[10px] uppercase">Fuel Cost</div>
                        <div className="text-pink-400 font-bold">$423.50</div>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-[#D4AF37]/10 rounded border border-[#D4AF37]/20">
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-xs">Minimum Rate</span>
                        <span className="text-[#D4AF37] font-bold">$2,541.00</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Resource Categories */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-pink-500/20 rounded mb-2 flex items-center justify-center">
                        <Calculator className="w-4 h-4 text-pink-400" />
                      </div>
                      <div className="text-white text-xs font-medium">Calculators</div>
                      <div className="text-white/40 text-[10px] mt-1">5 tools</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-blue-500/20 rounded mb-2 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="text-white text-xs font-medium">Templates</div>
                      <div className="text-white/40 text-[10px] mt-1">12 files</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-green-500/20 rounded mb-2 flex items-center justify-center">
                        <Download className="w-4 h-4 text-green-400" />
                      </div>
                      <div className="text-white text-xs font-medium">Downloads</div>
                      <div className="text-white/40 text-[10px] mt-1">8 files</div>
                    </div>
                    <div className="bg-white/5 p-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer">
                      <div className="w-8 h-8 bg-[#D4AF37]/20 rounded mb-2 flex items-center justify-center">
                        <HelpCircle className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div className="text-white text-xs font-medium">FAQ & Guides</div>
                      <div className="text-white/40 text-[10px] mt-1">20+ articles</div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
                    <div className="text-center">
                      <div className="text-pink-400 font-bold text-lg">25+</div>
                      <div className="text-white/40 text-[10px] uppercase">Tools</div>
                    </div>
                    <div className="text-center">
                      <div className="text-pink-400 font-bold text-lg">50+</div>
                      <div className="text-white/40 text-[10px] uppercase">Templates</div>
                    </div>
                    <div className="text-center">
                      <div className="text-pink-400 font-bold text-lg">Free</div>
                      <div className="text-white/40 text-[10px] uppercase">Updates</div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-sm text-center mt-4">
                Essential tools, calculators & templates
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" data-testid="hive-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
            Ready to <span className="text-[#D4AF37]">Join?</span>
          </h2>
          <p className="text-white/60 text-lg mb-6 max-w-2xl mx-auto">
            Get access to Build, Protect, Grow, Academy, and Resources — everything you need to launch, 
            maintain, and scale your trucking business.
          </p>
          
          {/* Module Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <span className="px-3 py-1 bg-[#CD7F32]/20 text-[#CD7F32] text-sm rounded">Build</span>
            <span className="px-3 py-1 bg-[#C0C0C0]/20 text-[#C0C0C0] text-sm rounded">Protect</span>
            <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-sm rounded">Grow</span>
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm rounded">Academy</span>
            <span className="px-3 py-1 bg-pink-500/20 text-pink-400 text-sm rounded">Resources</span>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/intake"
              className="w-full sm:w-auto px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
              data-testid="hive-cta-apply"
            >
              Apply to Join the Hive
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/hive/login"
              className="w-full sm:w-auto px-10 py-5 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-colors"
              data-testid="hive-cta-access"
            >
              Already a Member? Access Here
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsideTheHivePage;
